import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-hot-toast";
import { useCanUserReviewQuery, useSubmitReviewMutation } from "../../redux/api/productsApi";

const NewReviews = ({ productId, onSubmitted }) => {
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [open, setOpen] = useState(false);

    const [submitReview, { isLoading, error, isSuccess, reset }] = useSubmitReviewMutation();

    const { data } = useCanUserReviewQuery(productId, { skip: !productId });
    const canReview = data?.canReview

    // Backdrop ref and close-on-backdrop tracking
    const backdropRef = useRef(null);
    const shouldCloseRef = useRef(false);
    const onBackdropMouseDown = (e) => {
        if (e.target === backdropRef.current) {
            shouldCloseRef.current = true;
        } else {
            shouldCloseRef.current = false;
        }
    };
    const onBackdropMouseUp = (e) => {
        if (shouldCloseRef.current && e.target === backdropRef.current) {
            setOpen(false);
        }
        shouldCloseRef.current = false;
    };
    const onBackdropWheel = (e) => {
        e.stopPropagation();
    };

    // Lock background scroll when modal is open
    useEffect(() => {
        if (open) {
            const prevOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
                document.body.style.overflow = prevOverflow;
            };
        }
    }, [open]);

    // keep a stable ref to the callback to avoid retriggering effect
    const onSubmittedRef = useRef(onSubmitted);
    useEffect(() => { onSubmittedRef.current = onSubmitted; }, [onSubmitted]);

    useEffect(() => {
        if (error) {
            toast.error(error?.data?.message);
        }

        if (isSuccess) {
            toast.success("Review Posted");
            // clear form state
            setRating(0);
            setComment("");
            setOpen(false);
            // notify parent to refresh
            if (typeof onSubmittedRef.current === 'function') {
                onSubmittedRef.current();
            }
            // reset mutation so success doesn't fire again on re-render
            reset();
        }
    }, [error, isSuccess, reset]);

    const submitHandler = async () => {
        const reviewData = { rating, comment, productId };
        await submitReview(reviewData);
    };

    return (
        <div style={{ marginTop: '1rem' }}>
            {canReview && (
                <button
                    id="review_btn"
                    type="button"
                    className="View-btn"
                    style={{ marginTop: '0.25rem' }}
                    onClick={() => setOpen(true)}
                >
                    Submit Your Review
                </button>
            )}

            {open && ReactDOM.createPortal(
                <div
                    ref={backdropRef}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'rgba(0,0,0,0.55)',
                        zIndex: 2000,
                    }}
                    role="dialog"
                    aria-modal="true"
                    onMouseDown={onBackdropMouseDown}
                    onMouseUp={onBackdropMouseUp}
                    onWheel={onBackdropWheel}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                            display: 'flex',
                            flexDirection: 'column',
                            width: 'min(520px, 94vw)',
                            maxHeight: '90vh',
                            willChange: 'transform',
                            transform: 'translateZ(0)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 16px',
                                borderBottom: '1px solid #eee',
                                flexShrink: 0,
                            }}
                        >
                            <h5 style={{ margin: 0, fontWeight: 600 }}>Submit Review</h5>
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setOpen(false)}
                                style={{
                                    appearance: 'none',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '20px',
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    color: '#666',
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '16px', overflow: 'auto', flex: 1 }}>
                            {/* Star Rating */}
                            <div className="ratings" style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <i
                                        key={star}
                                        className="fa fa-star"
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '1.7rem',
                                            margin: '0 4px',
                                            color: star <= rating ? '#ffb829' : '#6c757d'
                                        }}
                                        onClick={() => setRating(star)}
                                    ></i>
                                ))}
                            </div>

                            {/* Comment Input */}
                            <textarea
                                name="review"
                                id="review"
                                style={{
                                    width: '100%',
                                    display: 'block',
                                    marginTop: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #ccc',
                                    padding: '10px 12px',
                                    fontFamily: 'inherit',
                                    outline: 'none',
                                }}
                                rows="4"
                                placeholder="Write your review here..."
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                            ></textarea>

                            {/* Submit Button */}
                            <button
                                id="new_review_btn"
                                className="View-btn"
                                style={{ width: '100%', marginTop: '12px', paddingTop: '10px', paddingBottom: '10px', fontWeight: 600 }}
                                disabled={isLoading || rating < 1 || !String(comment || '').trim()}
                                onClick={submitHandler}
                            >
                                {isLoading ? "Submitting..." : "Submit Review"}
                            </button>
                        </div>
                        {/* end body */}
                    </div>
                    {/* end content */}
                </div>,
                document.body
            )}
        </div>
    );
};

export default NewReviews;
